// app/components/privacy-policy/Hero.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function Hero() {
  const t = useTranslations("PrivacyPolicy");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // map pathname → path สำหรับ CMS (ตัด /en, /zh ด้านหน้า)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // default จาก i18n (ดีต่อ SEO / SSR)
  const defaultTitle = t("Hero.title");
  const defaultLastUpdate = t("Hero.lastUpdate");

  const [title, setTitle] = React.useState(defaultTitle);
  const [lastUpdate, setLastUpdate] = React.useState(defaultLastUpdate);

  // reset เมื่อ locale เปลี่ยน
  React.useEffect(() => {
    setTitle(defaultTitle);
    setLastUpdate(defaultLastUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // โหลด inline edits
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        if (map["privacy.hero.title"]) setTitle(map["privacy.hero.title"]);
        if (map["privacy.hero.lastUpdate"])
          setLastUpdate(map["privacy.hero.lastUpdate"]);
      } catch (err) {
        console.warn("[PrivacyPolicy Hero] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="policy-hero">
      <Container className="container">
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h1"
              data-aos="zoom-in"
              data-cms-key="privacy.hero.title"
              sx={{
                mb: 1,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>
            <Typography
              variant="body1"
              data-aos="zoom-in"
              data-cms-key="privacy.hero.lastUpdate"
            >
              {lastUpdate}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}